import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../components/ui';
import { useTranslation } from '../i18n';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateUserProfile } from '../store/slices/authSlice';

/**
 * The email verification link redirects to `/dashboard?verified=1` (the
 * backend picks the page, so any signed-in route may receive it). Once the
 * visitor is signed in this shows the success toast once, marks the user as
 * verified locally and removes the param with `replace`.
 * Mounted once, in `VerifiedParamHandler` (`App.tsx`); pages do not read it.
 */
export function useVerifiedParam(): void {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();
  const justVerified = searchParams.get('verified') === '1';
  const handled = useRef(false);

  useEffect(() => {
    if (!justVerified) {
      handled.current = false;
      return;
    }
    if (!isAuthenticated || handled.current) {
      return;
    }
    handled.current = true;
    dispatch(
      updateUserProfile({ email_verified_at: new Date().toISOString() })
    );
    showToast({ message: t('account.verify.success') });
    const next = new URLSearchParams(searchParams);
    next.delete('verified');
    setSearchParams(next, { replace: true });
    // Runs once per redirect; the param is removed above.
  }, [justVerified, isAuthenticated]);
}
